import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Award, Users, Clock, Shield, MapPin } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const About = () => {
  const handleContactClick = () => {
    window.location.href = "tel:0658864706";
  };

  const stats = [
    {
      number: "6+",
      label: "Années d'expérience",
      icon: Clock
    },
    {
      number: "50+",
      label: "Projets réalisés",
      icon: Award
    },
    {
      number: "100%",
      label: "Clients satisfaits",
      icon: Users
    }
  ];

  const values = [
    {
      title: "Excellence",
      description: "Nous nous engageons à fournir des finitions de la plus haute qualité pour chaque projet.",
      icon: Award
    },
    {
      title: "Proximité",
      description: "Une relation de confiance avec nos clients basée sur l'écoute et le conseil personnalisé.",
      icon: Users
    },
    {
      title: "Fiabilité",
      description: "Respect des délais, des budgets et engagement sur la qualité de nos prestations.",
      icon: Shield
    },
    {
      title: "Innovation",
      description: "Techniques modernes et matériaux écologiques pour des résultats durables.",
      icon: Clock
    }
  ];

  const competences = [
    "Peinture intérieure et extérieure",
    "Ravalement de façades",
    "Pose de faux plafonds",
    "Revêtements muraux et sols",
    "Application de crépis",
    "Finitions décoratives",
    "Conseils en décoration",
    "Préparation des supports"
  ];

  const interventionZones = [
    "Gries et environs",
    "Strasbourg",
    "Haguenau", 
    "Saverne",
    "Molsheim",
    "Sélestat"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary-glow to-secondary py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-white">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              Notre entreprise
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              À propos de JS Concept
            </h1>
            <p className="text-xl mb-8 text-white/90 leading-relaxed max-w-3xl mx-auto">
              Votre partenaire de confiance pour tous vos travaux de peinture et décoration dans le Bas-Rhin. 
              Une expertise reconnue au service de vos projets.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <CardTitle className="text-3xl font-bold text-primary">{stat.number}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground font-medium">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Histoire Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Notre Histoire</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Fondée à Gries dans le Bas-Rhin, <strong className="text-foreground">JS Concept</strong> est une entreprise familiale 
                  spécialisée dans la peinture et la décoration. Forte de plus de 6 années d'expérience, 
                  notre équipe s'est forgée une solide réputation grâce à son savoir-faire et son attention aux détails.
                </p>
                <p>
                  Nous intervenons aussi bien pour les particuliers que les professionnels, en proposant 
                  des solutions sur mesure adaptées à chaque projet. Notre philosophie : 
                  <em className="text-primary font-semibold">\"Les petits changements font les grosses différences...\"</em>
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://skyagent-artifacts.skywork.ai/page/g4fx3zsin9/images/Interieur%201.png" 
                alt="Équipe JS Concept au travail" 
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Nos Valeurs</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Les principes qui guident notre travail au quotidien
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-16 h-16 bg-accent text-accent-foreground rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Competences Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://static-us-img.skywork.ai/prod/user/head_picture/2008237094205820928_Interieur%202.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" 
                alt="Travaux de peinture JS Concept" 
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Nos Compétences</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Une expertise complète dans tous les domaines de la peinture et de la décoration, 
                de la préparation des supports aux finitions les plus sophistiquées.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {competences.map((competence, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-sm">{competence}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Zone d'Intervention</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Basée à Gries, notre entreprise intervient dans les secteurs de Gries et environs, 
              Strasbourg, Haguenau, Saverne, Molsheim, Sélestat. Nous nous déplaçons pour tous vos projets, 
              petits ou grands.
            </p>
          </div>
          
          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="text-center flex items-center justify-center">
                <MapPin className="h-6 w-6 mr-2" />
                Nos secteurs d'intervention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {interventionZones.map((zone, index) => (
                  <div key={index} className="flex items-center justify-center p-3 bg-muted rounded-lg">
                    <span className="font-medium">{zone}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Travaillons ensemble sur votre projet</h2>
          <p className="text-xl mb-8 opacity-90">
            Vous avez un projet de peinture ou décoration ? N'hésitez pas à nous contacter 
            pour un devis gratuit et des conseils personnalisés.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleContactClick}
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white px-8 py-4"
            >
              <Phone className="h-5 w-5 mr-2" />
              06 58 86 47 06
            </Button>
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary px-8 py-4"
            >
              <Link to="/contact">
                Nous contacter
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;